import { apiFetch, EXPLORER_BASE } from '../lib/api';
import { parseTonAmount } from '../lib/ton-format';
// CF-BONUS-V3-WITHDRAW-WARNING-V1: a cash withdrawal while a bonus is ACTIVE voids the bonus in the
// same backend transaction (backend/src/custodial/withdrawals.ts -> voidActiveBonusOnWithdrawalInTx;
// bonus-engine.ts sets sticky_nano='0', status='voided'). That is irreversible. This modal must not
// let a player trigger it unwarned.
//
// Scorer scope ruling 2026-08-16 0710: the warning must NOT ride on the app-level bonus render path,
// because the unresolved RCA failure mode is precisely "player HAS an active sticky bonus but the UI
// path failed to surface it". Inheriting `bonusView` would make the warning reliably absent for the
// exact players who most need it. So we do our own read here, and we fail CLOSED on unknown.
import { fetchBonusState } from '../lib/bonus-api';
// The decision itself is a pure module with a hermetic test — see lib/withdraw-bonus-guard.ts.
// Both the render path and the handleWithdraw re-check ask `mayAttemptWithdrawal`, so the two
// barriers cannot drift apart.
import { deriveBonusGuard, mayAttemptWithdrawal, type BonusGuard } from '../lib/withdraw-bonus-guard';
import { track } from '../lib/track';
import { useT, translate } from '../i18n';
import { isDragonTiger, isGerman, isBrazil } from '../lib/app-variant';
// CF-BRAND-LION-DIAMOND-V1: bundled diamond mark replaces platform 💎 emoji.
import cfDiamondMark from '../assets/brand/cf-diamond.svg';
import React, { useState, useEffect, useRef } from 'react';

// CF-WITHDRAWAL-UI-HONESTY-V1 (2026-05-26): added 'queued' step. Reserved for
// the timeout path where the backend status is still pending/submitted after
// POLL_TIMEOUT_MS — we do NOT have on-chain confirmation yet, so we MUST NOT
// show the ✅ "Withdrawal sent" UI. 'queued' renders a calm in-progress UI.
// The 'success' step is reserved EXCLUSIVELY for backend status === 'confirmed'.
// The in-flight 'review' state (requires_manual_review flag) renders a CONFIDENT
// "On its way!" 🚀 — never a premature ✅ "Sent" and never a scary/hedged claim
// (CF-WITHDRAWAL-COPY-CONFIDENT-V1, 2026-06-03; trust-first per PL #7531/#7533).
type Step = 'confirm' | 'processing' | 'queued' | 'review' | 'success' | 'error';
type WithdrawalStatus = 'pending' | 'submitted' | 'confirmed' | 'failed' | 'reversed';

interface Props {
    apiBase: string;
    address: string;        // Player's bound wallet (custodial dest)
    balanceNano: string;    // Full custodial balance, in nano
    onClose: () => void;
    onSuccess: () => void;
}

// CF-ZH-L10N-V1: rotating processing-status keys (resolved at render via t()).
const PROCESS_MESSAGE_KEYS = [
    'withdraw.processing.queueing',
    'withdraw.processing.submitting',
    'withdraw.processing.awaiting_confirmation',
];

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 180_000;   // 3 minutes — typical TON confirmation window with margin.

// CF-DEPOSIT-UX-CRITICAL-V2: Auto-retry helper for /withdraw when backend returns
// 503 with body.error === 'hot_wallet_balance_unavailable'. Up to 2 retries at
// 1500ms spacing. Reuses the same body across attempts (amount_nano + dest_address
// stay byte-identical). NEVER retries inside the catch path (network errors) — the
// outer try/catch in handleWithdraw routes through track.withdrawFail('network_error').
// NEVER retries on 409, 402, other 4xx, maintenance_mode, hot_wallet_unconfigured,
// temporary_insolvency_protection, or any non-503 status.
const WITHDRAW_503_RETRY_MAX = 2;
const WITHDRAW_503_RETRY_DELAY_MS = 1500;

async function withdrawWithRetry(
    apiBase: string,
    bodyJson: string,
): Promise<{ resp: Response; data: unknown }> {
    for (let attempt = 0; attempt <= WITHDRAW_503_RETRY_MAX; attempt++) {
        const resp = await apiFetch(`${apiBase}/api/v1/withdraw`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: bodyJson,
        });
        const parsed: unknown = await resp.json().catch(() => ({}));
        const is503HotWalletBalance =
            resp.status === 503 &&
            parsed != null &&
            typeof parsed === 'object' &&
            'error' in parsed &&
            (parsed as { error: unknown }).error === 'hot_wallet_balance_unavailable';
        if (!is503HotWalletBalance || attempt === WITHDRAW_503_RETRY_MAX) {
            return { resp, data: parsed };
        }
        await new Promise<void>((r) => setTimeout(r, WITHDRAW_503_RETRY_DELAY_MS));
    }
    throw new Error('withdrawWithRetry: unreachable');
}

// CF-WITHDRAW-DISPLAY-PRECISION-V1: the local 2-decimal `nanoToTon` truncation was
// removed. Money amounts now go through the canonical EXACT formatter in
// lib/ton-format.ts. `parseTonAmount` keeps the fail-loud {ok,reason} contract
// (R6 Binding 6: malformed economic values render an explicit invalid state, never
// a coerced zero) and shows the exact value (e.g. 4.325, not 4.32).

// CF-ZH-L10N-V1: maps backend error codes to localized copy. Module-level (no hook),
// so it resolves via the non-hook translate(). The `fallback` carries a raw backend
// err.message passthrough OR a localized default supplied by the caller.
function mapErrorCode(code: string | undefined, fallback: string): string {
    switch (code) {
        case 'maintenance_mode':
            return translate('withdraw.error.maintenance_mode');
        case 'insufficient_balance':
            return translate('withdraw.error.insufficient_balance');
        case 'temporary_insolvency_protection':
            return translate('withdraw.error.temporary_insolvency_protection');
        case 'invalid_dest_address':
            return translate('withdraw.error.invalid_dest_address');
        case 'hot_wallet_balance_unavailable':
            return translate('withdraw.error.hot_wallet_balance_unavailable');
        case 'hot_wallet_unconfigured':
            return translate('withdraw.error.hot_wallet_unconfigured');
        case 'invalid_amount_nano':
        case 'missing_amount_or_dest':
            return translate('withdraw.error.invalid_request');
        default:
            return fallback;
    }
}

export const WithdrawModal: React.FC<Props> = ({ apiBase, address, balanceNano, onClose, onSuccess }) => {
    const t = useT();
    const [step, setStep] = useState<Step>('confirm');
    const [msgIndex, setMsgIndex] = useState(0);
    const [progress, setProgress] = useState(0);
    const [resultMsg, setResultMsg] = useState('');
    const [txHash, setTxHash] = useState('');
    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // CF-BONUS-V3-WITHDRAW-WARNING-V1 R3 — there is NO mount-time bonus UI.
    //
    // PL ruling TG #13232: "not before, we dont want to scare them before they try to withdraw."
    // So we do not read, and show nothing, when the modal opens. The ONLY bonus read happens when
    // the player actually taps Withdraw — which is also precisely what Scorer's R2 blocker 1 demands
    // (the submit barrier must be fresh; a cached `clear` is not indefinite permission).
    //
    // One read, at the one moment it decides something. Non-null `interception` means that read came
    // back with something the player must see BEFORE anything is sent.
    const [interception, setInterception] = useState<BonusGuard | null>(null);
    const [submitBusy, setSubmitBusy] = useState(false);

    const balanceTonResult = parseTonAmount(balanceNano);
    // If the parent rendered the modal with a malformed balance, refuse to
    // operate: render an error card with retry/close. App.tsx's balance
    // validator (Binding 3) should prevent this from being reachable, but we
    // defend at this boundary too — no Withdraw on a value we cannot trust.
    const balanceInvalid = !balanceTonResult.ok;
    const balanceTon = balanceTonResult.ok ? balanceTonResult.ton : '—';

    // Cleanup any pending poll timer on unmount.
    useEffect(() => {
        return () => {
            if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
        };
    }, []);

    // Animate progress + rotate status message during processing.
    useEffect(() => {
        if (step !== 'processing') return;
        const msgTimer = setInterval(() => setMsgIndex(i => (i + 1) % PROCESS_MESSAGE_KEYS.length), 4000);
        const progTimer = setInterval(() => setProgress(p => Math.min(p + 2, 90)), 500);
        return () => { clearInterval(msgTimer); clearInterval(progTimer); };
    }, [step]);

    const finalizeQueued = () => {
        // CF-WITHDRAWAL-UI-HONESTY-V1 (2026-05-26): route to 'queued' step, not
        // 'success'. Polling timed out before backend confirmed; backend status
        // could be 'pending' (queued, NOT yet broadcast — see withdrawals.ts
        // initial insert) or 'submitted' (broadcast, awaiting confirmation).
        // Since the UI does not know which, we MUST NOT claim on-chain
        // processing or delivery. Neutral wording only.
        setResultMsg(translate('withdraw.queued.result_msg'));
        setStep('queued');
    };

    const pollStatus = (id: string, startedAt: number) => {
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
            finalizeQueued();
            return;
        }
        apiFetch(`${apiBase}/api/v1/withdraw/status/${id}`)
            .then(async (resp) => {
                if (!resp.ok) {
                    throw new Error(`status_http_${resp.status}`);
                }
                const body = await resp.json();
                const status: WithdrawalStatus = body.status;
                if (status === 'confirmed') {
                    setProgress(100);
                    setTxHash(typeof body.tx_hash === 'string' ? body.tx_hash : '');
                    setResultMsg(translate('withdraw.success.result_msg', { amount: balanceTon }));
                    setStep('success');
                    track.withdrawSuccess(Number(balanceNano) / 1e9);
                    return;
                }
                if (status === 'failed') {
                    setResultMsg(typeof body.failure_reason === 'string' && body.failure_reason
                        ? body.failure_reason
                        : translate('withdraw.error.failed_default'));
                    setStep('error');
                    track.withdrawFail(Number(balanceNano) / 1e9, 'status_failed');
                    return;
                }
                if (status === 'reversed') {
                    setResultMsg(translate('withdraw.error.reversed'));
                    setStep('error');
                    track.withdrawFail(Number(balanceNano) / 1e9, 'status_reversed');
                    return;
                }
                // pending or submitted — keep polling.
                pollTimerRef.current = setTimeout(() => pollStatus(id, startedAt), POLL_INTERVAL_MS);
            })
            .catch((err) => {
                console.error('🚨 withdraw_status_poll_failed', err);
                // Transient network blip — don't flip to error; retry on the next interval.
                pollTimerRef.current = setTimeout(() => pollStatus(id, startedAt), POLL_INTERVAL_MS);
            });
    };

    /**
     * CF-BONUS-V3-WITHDRAW-WARNING-V1 R3 — the submit barrier is FRESH, not cached.
     *
     * Scorer R2 blocker 1: the mount-time guard is the right first layer, but gating the POST on it
     * leaves a real race — modal opens, read says `clear`, a deposit/bonus credit lands while the
     * modal is still open, and the stale `clear` waves through a withdrawal that now destroys sticky
     * chips, unwarned. That is the SAME class as the original bug: a destructive action proceeding on
     * stale absence. A cached `clear` is not indefinite permission.
     *
     * So every submit re-reads `/api/v1/bonus` and re-derives. `acknowledgedStickyTon` is the amount
     * the player explicitly agreed to forfeit; if the FRESH risk is a different amount, the
     * acknowledgement does not carry — the warning re-renders with the new figure and nothing is sent.
     */
    const handleWithdraw = async (acknowledgedStickyTon: string | null = null) => {
        if (submitBusy) return;
        setSubmitBusy(true);
        let fresh: BonusGuard;
        try {
            // Never throws: every failure resolves to `unavailable` → `blocked` → no POST.
            fresh = deriveBonusGuard(await fetchBonusState(apiBase));
        } finally {
            setSubmitBusy(false);
        }
        // Unknown at submit time → fail closed and SAY SO. Silence is not permission.
        if (!mayAttemptWithdrawal(fresh)) { setInterception(fresh); return; }
        // Value at risk this click did not explicitly acknowledge — either brand-new risk, or an
        // amount that CHANGED since they agreed. Consent is to a quantity, not to a button.
        if (fresh.kind === 'at_risk' && acknowledgedStickyTon !== fresh.stickyTon) {
            setInterception(fresh); return;
        }
        setInterception(null);

        setStep('processing');
        setProgress(0);
        const amountTon = Number(balanceNano) / 1e9;
        track.withdrawInitiated(amountTon);

        try {
            // CF-DEPOSIT-UX-CRITICAL-V2: build body ONCE; withdrawWithRetry reuses
            // verbatim across attempts. Retry trigger is parsed HTTP 503 +
            // body.error === 'hot_wallet_balance_unavailable' only. Network errors
            // throw out of the helper and are caught by the outer catch path below.
            const withdrawBody = JSON.stringify({ amount_nano: balanceNano, dest_address: address });
            const withdrawResult = await withdrawWithRetry(apiBase, withdrawBody);
            const resp = withdrawResult.resp;
            const data = withdrawResult.data as any;

            if (!resp.ok) {
                setResultMsg(mapErrorCode(data.error, data.message || translate('withdraw.error.failed_default')));
                setStep('error');
                track.withdrawFail(amountTon, `http_${resp.status}`);
                return;
            }

            const wid = typeof data.withdrawal_id === 'string' ? data.withdrawal_id : null;
            if (!wid) {
                setResultMsg(translate('withdraw.error.missing_id_accepted'));
                setStep('error');
                track.withdrawFail(amountTon, 'missing_withdrawal_id');
                return;
            }

            const manualReview = !!data.requires_manual_review;
            if (manualReview) {
                // In-flight / not-yet-confirmed withdrawal. Confident "on its way" — NOT a premature fake
                // "Sent" before on-chain confirmation, and NOT scary (no fear-words, no support-contact doubt).
                setResultMsg(translate('withdraw.review.result_msg'));
                setStep('review');
                return;
            }
            pollStatus(wid, Date.now());
        } catch (err) {
            console.error('🚨 withdraw_post_failed', err);
            setResultMsg(translate('withdraw.error.network'));
            setStep('error');
            track.withdrawFail(amountTon, 'network_error');
        }
    };

    if (balanceInvalid) {
        // R6 Binding 6: never render a Withdraw CTA on a balance value we
        // cannot trust. App.tsx (Binding 3) should refuse to even mount the
        // modal with a malformed balance, but if it leaks through, fail-loud.
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm px-6">
                <div className="bg-[#1a1a2e] rounded-2xl p-6 w-full max-w-sm shadow-2xl border border-yellow-500/20">
                    <div className="text-center mb-4">
                        <div className="text-4xl mb-3">⚠️</div>
                        <h3 className="text-xl font-bold text-white mb-1">{t('withdraw.balance_invalid.title')}</h3>
                        <p className="text-sm text-slate-400 mt-2 leading-relaxed">
                            {t('withdraw.balance_invalid.body')}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-xl transition-colors"
                    >
                        {t('common.close_btn')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm px-6">
            <div className="bg-[#1a1a2e] rounded-2xl p-6 w-full max-w-sm shadow-2xl border border-yellow-500/20">

                {/* ─── Bonus interception (R3) — only ever reached by TAPPING Withdraw ─── */}
                {step === 'confirm' && interception !== null && (
                    <>
                        <div className="text-center mb-4">
                            <h3 className="text-xl font-bold text-white mb-1">
                                {interception.kind === 'at_risk'
                                    ? t('withdraw.bonus.at_risk_label')
                                    : t('withdraw.bonus.blocked_title')}
                            </h3>
                        </div>

                        {interception.kind === 'at_risk' && (
                            <>
                                <div className="bg-slate-800/50 rounded-xl p-3 mb-3 text-sm">
                                    <div className="flex justify-between mb-1.5">
                                        <span className="text-slate-400">{t('withdraw.confirm.you_receive')}</span>
                                        <span className="text-green-400 font-bold">
                                            {t('withdraw.confirm.balance_ton', { amount: balanceTon })}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">{t('withdraw.bonus.at_risk_label')}</span>
                                        <span className="text-yellow-400 font-bold">
                                            {t('withdraw.confirm.balance_ton', { amount: interception.stickyTon })}
                                        </span>
                                    </div>
                                </div>
                                <div className="text-slate-300 text-sm mb-2">
                                    {t('withdraw.bonus.forfeit_warning', { amount: interception.stickyTon })}
                                </div>
                                {interception.remainingTon !== null && (
                                    <div className="text-slate-400 text-xs mb-3">
                                        {t('withdraw.bonus.unlock_hint', { amount: interception.remainingTon })}
                                    </div>
                                )}
                                {/* Keeping the bonus is the DEFAULT. Forfeiting is secondary, explicit,
                                    and names the exact amount it costs. */}
                                <button
                                    onClick={() => { setInterception(null); onClose(); }}
                                    className={`w-full py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-xl transition-colors${isDragonTiger ? ' dt-cta-gradient' : isGerman ? ' de-cta-gradient' : isBrazil ? ' br-cta-gradient' : ''}`}
                                >
                                    {t('withdraw.bonus.keep_cta')}
                                </button>
                                <button
                                    onClick={() => handleWithdraw(interception.stickyTon)}
                                    disabled={submitBusy}
                                    className="w-full py-2.5 mt-2 border border-slate-600 text-slate-300 hover:bg-slate-700/40 rounded-xl transition-colors text-sm font-semibold disabled:opacity-60"
                                >
                                    {submitBusy
                                        ? t('withdraw.bonus.checking')
                                        : t('withdraw.bonus.forfeit_cta', { amount: interception.stickyTon })}
                                </button>
                            </>
                        )}

                        {interception.kind === 'blocked' && (
                            <>
                                <div className="text-slate-400 text-sm mb-3">{t('withdraw.bonus.blocked_body')}</div>
                                {/* No control here submits. Retry re-enters handleWithdraw, which reads
                                    fresh again and fails closed again if it still cannot tell. */}
                                <button
                                    onClick={() => handleWithdraw()}
                                    disabled={submitBusy}
                                    className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-xl transition-colors disabled:opacity-60"
                                >
                                    {submitBusy ? t('withdraw.bonus.checking') : t('withdraw.bonus.retry')}
                                </button>
                                <button
                                    onClick={() => { setInterception(null); onClose(); }}
                                    className="w-full py-2 mt-2 text-slate-400 hover:text-white transition-colors text-sm"
                                >
                                    {t('common.cancel_btn')}
                                </button>
                            </>
                        )}
                    </>
                )}

                {/* ─── Confirm ─── */}
                {step === 'confirm' && interception === null && (
                    <>
                        <div className="text-center mb-4">
                            <div className="mb-3"><img src={cfDiamondMark} alt="" className="w-11 h-11 mx-auto" /></div>
                            <h3 className="text-xl font-bold text-white mb-1">{t('withdraw.confirm.title')}</h3>
                        </div>
                        <div className="bg-slate-800/50 rounded-xl p-3 mb-4 text-sm">
                            <div className="flex justify-between mb-1.5">
                                <span className="text-slate-400">{t('withdraw.confirm.you_receive')}</span>
                                <span className="text-yellow-400 font-bold">{t('withdraw.confirm.balance_ton', { amount: balanceTon })}</span>
                            </div>
                            {/* CF-WD-CROSS-DEVICE-DEST-V1: show the destination — always the
                                account's BOUND wallet (the one that deposited), regardless of
                                which wallet is connected on this device. */}
                            <div className="flex justify-between mb-1.5">
                                <span className="text-slate-400">{t('withdraw.confirm.to_your_wallet')}</span>
                                <span className="text-white font-mono text-xs">{address.slice(0, 4)}…{address.slice(-4)}</span>
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                                {t('withdraw.confirm.no_fees')}
                            </div>
                        </div>
                        {/* CF-DRAGONTIGER-THEME-V1 Phase B (D4): the 提现 confirm CTA
                            gets the red→gold gradient via .dt-cta-gradient — appended
                            ONLY under the variant so the EN/default flat gold
                            (bg-yellow-500) is unchanged.
                            CF-GERMAN-VARIANT-V1: the Auszahlen CTA gets the champagne
                            gradient via .de-cta-gradient under the german variant. */}
                        {/* CF-BONUS-V3-WITHDRAW-WARNING-V1 R3 — PL ruling TG #13232 (07:26:18Z):
                            "lets make a good warning or notification when they make the witdrawal.
                            but not before, we dont want to scare them before they try to withdraw."
                            So the confirm screen is UNCHANGED for everyone. Nothing about the bonus is
                            shown here. The check fires on the TAP, in handleWithdraw, against a FRESH
                            read — which is also exactly what Scorer's R2 blocker 1 requires. One
                            mechanism now serves both: no pre-emptive scare, and no stale permission. */}
                        <button
                            /* Arrow wrapper is REQUIRED: `onClick={handleWithdraw}` would pass the
                               React SyntheticEvent as `acknowledgedStickyTon`. */
                            onClick={() => handleWithdraw()}
                            disabled={submitBusy}
                            className={`w-full py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-xl transition-colors disabled:opacity-60${isDragonTiger ? ' dt-cta-gradient' : isGerman ? ' de-cta-gradient' : isBrazil ? ' br-cta-gradient' : ''}`}
                        >
                            {submitBusy ? t('withdraw.bonus.checking') : t('withdraw.confirm.cta', { amount: balanceTon })}
                        </button>
                        <button
                            onClick={onClose}
                            className="w-full py-2 mt-2 text-slate-400 hover:text-white transition-colors text-sm"
                        >
                            {t('common.cancel_btn')}
                        </button>
                    </>
                )}

                {/* ─── Processing ─── */}
                {step === 'processing' && (
                    <>
                        <div className="text-center mb-6">
                            <div className="text-4xl mb-3 animate-pulse">⏳</div>
                            <h3 className="text-lg font-bold text-white mb-1">{t('withdraw.processing.title')}</h3>
                            <p className="text-slate-400 text-sm">{t(PROCESS_MESSAGE_KEYS[msgIndex])}</p>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-2 mb-4">
                            <div
                                className="bg-yellow-500 h-2 rounded-full transition-all duration-500"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                        <p className="text-center text-xs text-slate-500">
                            {t('withdraw.processing.reassurance')}
                        </p>
                    </>
                )}

                {/* ─── Queued (CF-WITHDRAWAL-UI-HONESTY-V1) ─── */}
                {step === 'queued' && (
                    <>
                        <div className="text-center mb-6">
                            <div className="text-4xl mb-3">⏳</div>
                            <h3 className="text-lg font-bold text-white mb-1">{t('withdraw.queued.title')}</h3>
                            <p className="text-slate-400 text-sm mt-2">{resultMsg}</p>
                        </div>
                        <button
                            onClick={() => { onSuccess(); onClose(); }}
                            className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-xl transition-colors"
                        >
                            {t('common.close_btn')}
                        </button>
                        <p className="text-xs text-slate-500 mt-3 leading-relaxed text-center">
                            {t('withdraw.queued.footer')}
                        </p>
                    </>
                )}

                {/* ─── On its way (in-flight / not-yet-confirmed; confident, NEVER a premature fake "Sent") — CF-WITHDRAWAL-RELIABILITY-LONGTERM-V1 Phase-1 ─── */}
                {step === 'review' && (
                    <>
                        <div className="text-center mb-6">
                            <div className="text-4xl mb-3">🚀</div>
                            <h3 className="text-lg font-bold text-white mb-1">{t('withdraw.review.title')}</h3>
                            <p className="text-slate-400 text-sm mt-2">{resultMsg}</p>
                        </div>
                        <button
                            onClick={() => { onSuccess(); onClose(); }}
                            className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-xl transition-colors"
                        >
                            {t('common.done_btn')}
                        </button>
                    </>
                )}

                {/* ─── Success (confirmed only) ─── */}
                {step === 'success' && (
                    <>
                        <div className="text-center mb-6">
                            <div className="text-4xl mb-3">✅</div>
                            <h3 className="text-lg font-bold text-white mb-1">
                                {t('withdraw.success.title')}
                            </h3>
                            <p className="text-slate-400 text-sm mt-2">{resultMsg}</p>
                            {txHash ? (
                                <a
                                    href={`${EXPLORER_BASE}/transaction/${encodeURIComponent(txHash)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-yellow-400 text-xs font-semibold mt-3 inline-block"
                                >
                                    {t('withdraw.success.explorer_link')}
                                </a>
                            ) : null}
                        </div>
                        <button
                            onClick={() => { onSuccess(); onClose(); }}
                            className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-xl transition-colors"
                        >
                            {t('common.done_btn')}
                        </button>
                        <p className="text-xs text-slate-500 mt-3 leading-relaxed text-center">
                            {t('withdraw.success.footer')}
                        </p>
                    </>
                )}

                {/* ─── Error ─── */}
                {step === 'error' && (
                    <>
                        <div className="text-center mb-6">
                            <div className="text-4xl mb-3">⚠️</div>
                            <h3 className="text-lg font-bold text-white mb-1">{t('withdraw.error.title')}</h3>
                            <p className="text-slate-400 text-sm">{resultMsg}</p>
                            <p className="text-xs text-slate-500 mt-2">
                                {t('withdraw.error.reassurance')}
                            </p>
                        </div>
                        <button
                            onClick={() => { setStep('confirm'); setProgress(0); setResultMsg(''); }}
                            className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-xl transition-colors"
                        >
                            {t('common.retry_btn')}
                        </button>
                        <button
                            onClick={onClose}
                            className="w-full py-2 mt-2 text-slate-400 hover:text-white transition-colors text-sm"
                        >
                            {t('common.close_btn')}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};
